import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsuranceUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [docs, setDocs] = useState([]);
  const [company, setCompany] = useState("");
  const [policy, setPolicy] = useState("");
  const [exp, setExp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/insurance-upload/${token}`).then((r) => { setInfo(r.data); setCompany(r.data.company || ""); })
      .catch(() => setErr("This link is invalid or has expired. Please contact AutoHire STL."));
  }, [token]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/insurance-upload/${token}/upload`, fd);
      setDocs((p) => [...p, `${process.env.REACT_APP_BACKEND_URL}${data.url}`]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed — try again."); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!docs.length) { toast.error("Please add a photo of your insurance card first."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/insurance-upload/${token}/submit`,
        { doc_urls: docs, company, policy_number: policy, expiration_date: exp });
      toast.success(data.message);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit — try again."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center">{err}</div>;
  if (!info) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" size={28} /></div>;
  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center gap-3">
      <ShieldCheck className="text-emerald-400" size={44} />
      <h1 className="font-display font-bold text-2xl">Insurance Received</h1>
      <p className="text-white/60 text-sm max-w-md">Thank you{info.name ? `, ${info.name}` : ""}! AutoHire will review and verify your updated insurance shortly.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white p-4 pb-16">
      <div className="max-w-md mx-auto space-y-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-[#F2B705]">Update Your Insurance</h1>
          <p className="text-white/60 text-sm">For your rental — {info.vehicle}. Upload clear photos of your current insurance card (front/back) or a PDF of your policy.</p>
        </div>
        <label data-testid="ins-upload-drop" className="block rounded-xl border-2 border-dashed border-white/20 hover:border-[#F2B705]/60 p-6 text-center cursor-pointer transition-colors">
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <Camera className="mx-auto text-white/40 mb-1" size={26} />
          <span className="text-sm text-white/70 font-semibold">{busy ? "Uploading…" : `Tap to add insurance card photo${docs.length ? ` — ${docs.length} added ✓` : ""}`}</span>
        </label>
        {docs.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {docs.map((u, i) => <img key={i} src={u} alt="doc" className="h-20 w-full object-cover rounded border border-emerald-500/40" />)}
          </div>
        )}
        <div className="grid grid-cols-1 gap-2.5">
          <label className="text-xs text-white/60">Insurance company
            <input data-testid="ins-up-company" className="mt-1 w-full bg-[#0C0C0C] border border-white/15 rounded px-3 py-2 text-white" value={company} onChange={(e) => setCompany(e.target.value)} /></label>
          <label className="text-xs text-white/60">Policy number
            <input data-testid="ins-up-policy" className="mt-1 w-full bg-[#0C0C0C] border border-white/15 rounded px-3 py-2 text-white" value={policy} onChange={(e) => setPolicy(e.target.value)} /></label>
          <label className="text-xs text-white/60">Policy expiration date
            <input data-testid="ins-up-exp" type="date" className="mt-1 w-full bg-[#0C0C0C] border border-white/15 rounded px-3 py-2 text-white" value={exp} onChange={(e) => setExp(e.target.value)} /></label>
        </div>
        <button data-testid="ins-up-submit" onClick={submit} disabled={busy}
          className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-xl hover:bg-white transition-colors disabled:opacity-50">
          {busy ? "Working…" : "Submit Updated Insurance"}
        </button>
      </div>
    </div>
  );
}
